import * as THREE from 'three';

// TRODDEN-GROUND TRAIL MAP. The owner's ask: grass you walk through stays flattened for ~60s and
// slowly rises back. Implementation: a small world-anchored intensity map around the player (128^2
// texels at 0.6m = a 76.8m window) -- the player STAMPS it as they move, every cell DECAYS over
// TRAIL_SEC, and shaders read it with ONE texture sample (no per-blade state, no point lists to
// scan). The window recenters by shifting the array, so the trail survives as you roam; anything
// that scrolls out of the window just recovers early. Grass flattens by it; snow tints packed
// trails by it (same map, same uniforms).

const RES = 128;
const TEXEL = 0.6;
export const TRAIL_SIZE = RES * TEXEL; // 76.8m window
const TRAIL_SEC = 60;                  // full recovery time after a footstep

// Shared by every material that reads the trail (grass styles + the terrain splat).
export const trailUniforms = {
  uTrailTex: { value: null as THREE.DataTexture | null },
  uTrailRect: { value: new THREE.Vector4(0, 0, 1 / TRAIL_SIZE, 0) }, // (x0, z0, 1/size, enabled)
};

// Sample helper (inject once per shader): 0 = untouched, 1 = freshly trodden.
export const TRAIL_GLSL = /* glsl */ `
uniform sampler2D uTrailTex;
uniform vec4 uTrailRect;
float oxTrail(vec2 wxz){
  if (uTrailRect.w < 0.5) return 0.0;
  vec2 uv = (wxz - uTrailRect.xy) * uTrailRect.z;
  if (uv.x <= 0.0 || uv.x >= 1.0 || uv.y <= 0.0 || uv.y >= 1.0) return 0.0;
  return texture2D(uTrailTex, uv).r;
}`;

export function bindTrailUniforms(shader: { uniforms: Record<string, THREE.IUniform> }): void {
  shader.uniforms.uTrailTex = trailUniforms.uTrailTex;
  shader.uniforms.uTrailRect = trailUniforms.uTrailRect;
}

export class TrailMap {
  private readonly data = new Uint8Array(RES * RES);
  private readonly tex: THREE.DataTexture;
  private x0 = 0;
  private z0 = 0;
  private decayCarry = 0;
  private lastStampX = NaN;
  private lastStampZ = NaN;

  constructor() {
    this.tex = new THREE.DataTexture(this.data, RES, RES, THREE.RedFormat, THREE.UnsignedByteType);
    this.tex.magFilter = THREE.LinearFilter;
    this.tex.minFilter = THREE.LinearFilter;
    this.tex.needsUpdate = true;
    trailUniforms.uTrailTex.value = this.tex;
  }

  // Per frame from the main loop. `stamping` false while flying/dead (no ghost trails).
  update(dt: number, px: number, pz: number, stamping: boolean): void {
    if (trailUniforms.uTrailRect.value.w < 0.5) { // first update anchors the window on the player
      this.anchor(px, pz);
      trailUniforms.uTrailRect.value.w = 1;
    }
    const cx = this.x0 + TRAIL_SIZE / 2, cz = this.z0 + TRAIL_SIZE / 2;
    if (Math.abs(px - cx) > TRAIL_SIZE * 0.28 || Math.abs(pz - cz) > TRAIL_SIZE * 0.28) this.recenter(px, pz);
    if (stamping && (Number.isNaN(this.lastStampX) || Math.abs(px - this.lastStampX) + Math.abs(pz - this.lastStampZ) > 0.3)) {
      this.lastStampX = px;
      this.lastStampZ = pz;
      this.stamp(px, pz, 0.55);
    }
    // decay: 255 -> 0 over TRAIL_SEC (integer steps, carry the fraction)
    this.decayCarry += dt * (255 / TRAIL_SEC);
    const dec = this.decayCarry | 0;
    if (dec > 0) {
      this.decayCarry -= dec;
      const d = this.data;
      let touched = false;
      for (let i = 0; i < d.length; i++) {
        const v = d[i];
        if (v > 0) { d[i] = v > dec ? v - dec : 0; touched = true; }
      }
      if (touched) this.tex.needsUpdate = true;
    }
  }

  // Stamp a footprint at an arbitrary world position (a REMOTE player) into the shared trail. Bounds-clamped, so
  // a remote outside the local window is a harmless no-op; it never recenters (the window follows the LOCAL player).
  stampAt(px: number, pz: number): void { if (trailUniforms.uTrailRect.value.w >= 0.5) this.stamp(px, pz, 0.55); }

  private stamp(px: number, pz: number, r: number): void {
    const gx = (px - this.x0) / TEXEL, gz = (pz - this.z0) / TEXEL, rr = r / TEXEL;
    const xa = Math.max(0, Math.floor(gx - rr)), xb = Math.min(RES - 1, Math.ceil(gx + rr));
    const za = Math.max(0, Math.floor(gz - rr)), zb = Math.min(RES - 1, Math.ceil(gz + rr));
    for (let z = za; z <= zb; z++) {
      for (let x = xa; x <= xb; x++) {
        const dx = x + 0.5 - gx, dz = z + 0.5 - gz;
        if (dx * dx + dz * dz <= rr * rr) this.data[z * RES + x] = 255;
      }
    }
    this.tex.needsUpdate = true;
  }

  private anchor(px: number, pz: number): void {
    this.x0 = Math.round((px - TRAIL_SIZE / 2) / TEXEL) * TEXEL;
    this.z0 = Math.round((pz - TRAIL_SIZE / 2) / TEXEL) * TEXEL;
    trailUniforms.uTrailRect.value.set(this.x0, this.z0, 1 / TRAIL_SIZE, trailUniforms.uTrailRect.value.w);
  }

  // Shift the window to keep the player inside it, PRESERVING the trail (array copy by texel offset).
  private recenter(px: number, pz: number): void {
    const nx0 = Math.round((px - TRAIL_SIZE / 2) / TEXEL) * TEXEL;
    const nz0 = Math.round((pz - TRAIL_SIZE / 2) / TEXEL) * TEXEL;
    const sx = Math.round((nx0 - this.x0) / TEXEL), sz = Math.round((nz0 - this.z0) / TEXEL);
    const src = this.data;
    const out = new Uint8Array(RES * RES);
    for (let z = 0; z < RES; z++) {
      const oz = z + sz;
      if (oz < 0 || oz >= RES) continue;
      for (let x = 0; x < RES; x++) {
        const ox = x + sx;
        if (ox < 0 || ox >= RES) continue;
        out[z * RES + x] = src[oz * RES + ox];
      }
    }
    src.set(out);
    this.x0 = nx0;
    this.z0 = nz0;
    trailUniforms.uTrailRect.value.set(nx0, nz0, 1 / TRAIL_SIZE, trailUniforms.uTrailRect.value.w);
    this.tex.needsUpdate = true;
  }
}
